import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  Text,
  VStack,
  useTheme,
  Box,
  Spinner,
  Center,
  Button,
} from "@chakra-ui/react";
import { MdOutlineAssignment } from "react-icons/md";
import { useDispatch } from "react-redux";
import { getUrlForPDF } from "../../../store/slice/openPdfSlice";
import { Document, Page, pdfjs } from "react-pdf";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const OpenFileModal = ({ isOpen, onClose, tempfiles, orifiles, docType }) => {
  const theme = useTheme();
  const { fileBorderColor } = theme.colors.pallete;
  const dispatch = useDispatch();

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const handleFileClick = async (file) => {
    setSelectedFile(file);
    if (docType === "assignment" || docType === "live") {
      try {
        const res = await dispatch(
          getUrlForPDF({ docId: file.id, docType })
        ).unwrap();
        setFileUrl(res?.data?.getUrl || null);
      } catch (err) {
        console.error("Failed to fetch PDF URL", err);
        setFileUrl(null);
      }
    } else {
      setFileUrl(file.url);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const changePage = (offset) =>
    setPageNumber((prev) => Math.min(Math.max(prev + offset, 1), numPages));

  const handleOnClose = () => {
    setSelectedFile(null);
    setFileUrl(null);
    setNumPages(null);
    setPageNumber(1);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      size={selectedFile ? "xl" : "md"}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center" fontSize="18px">
          Files
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody mb={5}>
          {selectedFile ? (
            <VStack spacing={4} align="stretch" h="full">
              <HStack justify="space-between">
                <Text fontSize="14px" fontWeight={600}>
                  {selectedFile.name || selectedFile.fileName}
                </Text>

                {numPages > 1 && (
                  <HStack justify="center" mt={2} spacing={4}>
                    <Button
                      onClick={() => changePage(-1)}
                      isDisabled={pageNumber <= 1}
                    >
                      Previous
                    </Button>
                    <Text>
                      Page {pageNumber} of {numPages}
                    </Text>
                    <Button
                      onClick={() => changePage(1)}
                      isDisabled={pageNumber >= numPages}
                    >
                      Next
                    </Button>
                  </HStack>
                )}

                <Text
                  fontSize="12px"
                  color="blue.500"
                  cursor="pointer"
                  onClick={handleOnClose}
                >
                  Back to files
                </Text>
              </HStack>

              {!fileUrl ? (
                <Center py={10}>
                  <Spinner />
                </Center>
              ) : (
                <Box overflow="auto" maxH="60vh">
                  <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <Center>
                        <Spinner />
                      </Center>
                    }
                  >
                    <Page pageNumber={pageNumber} />
                  </Document>
                </Box>
              )}
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              {tempfiles && tempfiles.length > 0 ? (
                tempfiles.map((file, index) => (
                  <HStack
                    key={index}
                    px="6px"
                    py="10px"
                    h="34px"
                    borderRadius="4px"
                    borderWidth="1px"
                    borderColor={fileBorderColor}
                    justify="space-between"
                    onClick={() => handleFileClick(orifiles[index])}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                  >
                    <HStack spacing={2}>
                      <MdOutlineAssignment size={20} />
                      <Text fontSize="12px" fontWeight={500} noOfLines={1}>
                        {file.name || file.fileName}
                      </Text>
                    </HStack>
                  </HStack>
                ))
              ) : (
                <Text>No files available</Text>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default OpenFileModal;
